import { gsePricingApi } from 'app/services/api';
import _ from 'lodash';

import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { extractErrorMessage } from '../marginCenter.actionUtils';
import { GSE_PULL_HISTORY_QUERIES } from '../marginCenter.constants';
import { mapFeedPulls } from '../marginCenter.mappers';

export const FEED_PULLS_ACTION_TYPE = createNamedActions('marginCenter/feedPulls', {
  SET_FEED_PULLS_LOADING: 'SET_FEED_PULLS_LOADING',
  GET_FEED_PULLS: 'GET_FEED_PULLS',
  SET_PULL_GRID_LOADING: 'SET_PULL_GRID_LOADING',
  GET_PULL_GRID: 'GET_PULL_GRID',
  SET_PULL_GRID_ERROR: 'SET_PULL_GRID_ERROR',
  CLEAR_PULL_GRID: 'CLEAR_PULL_GRID',
  SET_PULL_SRP_MATRIX_LOADING: 'SET_PULL_SRP_MATRIX_LOADING',
  GET_PULL_SRP_MATRIX: 'GET_PULL_SRP_MATRIX',
  SET_PULL_SRP_MATRIX_ERROR: 'SET_PULL_SRP_MATRIX_ERROR',
  SET_PULL_SPEC_POOLS_LOADING: 'SET_PULL_SPEC_POOLS_LOADING',
  GET_PULL_SPEC_POOLS: 'GET_PULL_SPEC_POOLS',
  SET_PULL_SPEC_POOLS_ERROR: 'SET_PULL_SPEC_POOLS_ERROR',
});

const resolveSettled = result => (result.status === 'fulfilled' ? (result.value?.data ?? []) : []);

// Feeds & Sources (LEN-88): the read-only view of the GSE pull data — current feed freshness (GET
// gse-pricing/freshness) plus every previous pull across all statuses (one GET
// gse-pricing/pulls?status= per GsePullStatus, merged; see GSE_PULL_HISTORY_QUERIES). Each call
// degrades INDEPENDENTLY (Promise.allSettled) so one status 403/404/erroring never blanks the
// rest — a failed leg contributes an empty list, never a fabricated row, and the page still
// renders whatever did resolve. There is no all-statuses history endpoint yet, so the fan-out is
// deliberate.
export const getFeedPullsAction = catchAsyncActionError(() => async dispatch => {
  dispatch({ type: FEED_PULLS_ACTION_TYPE.SET_FEED_PULLS_LOADING, payload: true });

  const [freshnessResult, ...pullResults] = await Promise.allSettled([
    gsePricingApi.getFreshness(),
    ...GSE_PULL_HISTORY_QUERIES.map(query => gsePricingApi.getPulls(query)),
  ]);

  const pulls = pullResults.flatMap(resolveSettled);
  const payload = mapFeedPulls({ freshness: resolveSettled(freshnessResult), pulls });

  return dispatch({ type: FEED_PULLS_ACTION_TYPE.GET_FEED_PULLS, payload });
});

// LEN-88: the per-pull grid drill-down behind a "Recent pulls" row. Fetches the pull's priced grid
// (GET gse-pricing/pulls/{id}/grid). pullId is tracked from the first dispatch so the drawer can show
// the selected row's metadata while the grid loads; a 404 / error surfaces inline in the drawer, never
// the global toast (SKIP_FORBIDDEN keeps a missing-claim 403 inline too).
export const getPullGridAction = gsePricingPullId => async dispatch => {
  dispatch({ type: FEED_PULLS_ACTION_TYPE.SET_PULL_GRID_LOADING, payload: gsePricingPullId });

  try {
    const { data } = await gsePricingApi.getPullGrid(gsePricingPullId);

    return dispatch({
      type: FEED_PULLS_ACTION_TYPE.GET_PULL_GRID,
      payload: { pullId: gsePricingPullId, data },
    });
  } catch (error) {
    return dispatch({
      type: FEED_PULLS_ACTION_TYPE.SET_PULL_GRID_ERROR,
      payload: { pullId: gsePricingPullId, errorMessage: extractErrorMessage(error) },
    });
  }
};

export const clearPullGridAction = () => ({ type: FEED_PULLS_ACTION_TYPE.CLEAR_PULL_GRID });

// LEN-105: the SRP explorer behind an SRP-feed pull (GET gse-pricing/pulls/{id}/srp-matrix). Re-dispatched
// on every product / state / occupancy / note-rate-window change, so responses can land out of order: each
// request carries an id and the reducer drops any response that is no longer the latest.
export const getPullSrpMatrixAction =
  (gsePricingPullId, filters = {}) =>
  async dispatch => {
    const requestId = _.uniqueId('srp-matrix-');

    dispatch({
      type: FEED_PULLS_ACTION_TYPE.SET_PULL_SRP_MATRIX_LOADING,
      payload: { pullId: gsePricingPullId, requestId },
    });

    try {
      const { data } = await gsePricingApi.getPullSrpMatrix(gsePricingPullId, filters);

      return dispatch({
        type: FEED_PULLS_ACTION_TYPE.GET_PULL_SRP_MATRIX,
        payload: { pullId: gsePricingPullId, requestId, data },
      });
    } catch (error) {
      return dispatch({
        type: FEED_PULLS_ACTION_TYPE.SET_PULL_SRP_MATRIX_ERROR,
        payload: { pullId: gsePricingPullId, requestId, errorMessage: extractErrorMessage(error) },
      });
    }
  };

// Re-dispatched on every product / execution change, so responses can land out of order: like the SRP
// explorer, each request carries an id and the reducer keeps only the latest one's response.
export const getPullSpecPoolsAction =
  (gsePricingPullId, selection = {}) =>
  async dispatch => {
    const requestId = _.uniqueId('spec-pools-');

    dispatch({
      type: FEED_PULLS_ACTION_TYPE.SET_PULL_SPEC_POOLS_LOADING,
      payload: { pullId: gsePricingPullId, requestId },
    });

    try {
      const { data } = await gsePricingApi.getPullSpecifiedPools(gsePricingPullId, selection);

      return dispatch({
        type: FEED_PULLS_ACTION_TYPE.GET_PULL_SPEC_POOLS,
        payload: { pullId: gsePricingPullId, requestId, data },
      });
    } catch (error) {
      return dispatch({
        type: FEED_PULLS_ACTION_TYPE.SET_PULL_SPEC_POOLS_ERROR,
        payload: { pullId: gsePricingPullId, requestId, errorMessage: extractErrorMessage(error) },
      });
    }
  };
